import { Component, ElementRef, EventEmitter, HostListener, Input, Output, OnChanges, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";

@Component({
  selector: "app-multi-select-dropdown",
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: "./multi-select-dropdown.component.html",
  styleUrls: ["./multi-select-dropdown.component.scss"],
})
export class MultiSelectDropdownComponent implements OnInit, OnChanges {
  @Input() label = "";
  @Input() items: any[] = [];
  @Input() valueProperty = "id";
  @Input() displayProperty = "name";
  @Input() placeholder = "Select an option...";
  @Input() isMultiSelect = false;

  @Input() model: any;
  @Output() modelChange = new EventEmitter<any>();

  isOpen = false;
  selectedItemDisplay = "";

  // 1. Variable to hold search text
  searchText = "";

  @Input() showSelectAll = true;

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.updateSelectedItemDisplay();
  }

  ngOnChanges() {
    this.updateSelectedItemDisplay();
  }

  // 2. Getter to filter the items based on search text
  get filteredItems() {
    if (!this.searchText) {
      return this.items;
    }
    return this.items.filter(item => item[this.displayProperty].toLowerCase().includes(this.searchText.toLowerCase()));
  }

  // Helper to get the actual objects for the selected IDs (for chips)
  getSelectedItems(): any[] {
    if (!this.model || !this.items) return [];
    if (this.isMultiSelect) {
      const modelArray = this.model as any[];
      return this.items.filter(item => modelArray.includes(item[this.valueProperty]));
    }
    return [];
  }

  updateSelectedItemDisplay() {
    if (!this.items || !this.model) {
      this.selectedItemDisplay = this.placeholder;
      return;
    }

    if (this.isMultiSelect) {
      const modelArray = this.model as any[];
      if (modelArray.length === 0) {
        this.selectedItemDisplay = this.placeholder;
      } else {
        this.selectedItemDisplay = `${modelArray.length} Selected`;
      }
    } else {
      const selected = this.items.find(item => item[this.valueProperty] === this.model);
      this.selectedItemDisplay = selected ? selected[this.displayProperty] : this.placeholder;
    }
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.searchText = "";
    }
  }

  selectItem(item: any) {
    const selectedValue = item[this.valueProperty];

    if (this.isMultiSelect) {
      const modelArray = Array.isArray(this.model) ? [...this.model] : [];
      const index = modelArray.indexOf(selectedValue);

      if (index > -1) {
        modelArray.splice(index, 1);
      } else {
        modelArray.push(selectedValue);
      }
      this.model = modelArray;
    } else {
      this.model = selectedValue;
      this.isOpen = false;
      this.searchText = "";
    }

    this.modelChange.emit(this.model);
    this.updateSelectedItemDisplay();
  }

  removeItem(item: any, event: Event) {
    event.stopPropagation(); // Prevent dropdown toggle
    const selectedValue = item[this.valueProperty];
    if (this.isMultiSelect && Array.isArray(this.model)) {
      const modelArray = [...this.model];
      const index = modelArray.indexOf(selectedValue);
      if (index > -1) {
        modelArray.splice(index, 1);
        this.model = modelArray;
        this.modelChange.emit(this.model);
        this.updateSelectedItemDisplay();
      }
    }
  }

  toggleSelectAll() {
    if (!this.isMultiSelect) return;

    if (this.isAllSelected()) {
      this.model = [];
    } else {
      this.model = this.items.map(item => item[this.valueProperty]);
    }
    this.modelChange.emit(this.model);
    this.updateSelectedItemDisplay();
  }

  isAllSelected(): boolean {
    if (!this.isMultiSelect || !this.items || this.items.length === 0) return false;
    if (!this.model || !Array.isArray(this.model)) return false;
    return this.items.every(item => this.model.includes(item[this.valueProperty]));
  }

  isSelected(item: any): boolean {
    if (!this.model) return false;
    const value = item[this.valueProperty];
    if (this.isMultiSelect) {
      return (this.model as any[]).includes(value);
    }
    return this.model === value;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.searchText = "";
    }
  }
}
